import React, { useEffect, useState } from "react";
import Unauthorized from "./Unauthorized";

interface AuthState {
  state: {
    isAuthenticated: boolean;
    isPending?: boolean;
    profile: {
      sub?: string;
      email?: string;
      emailVerified?: string; // String "true" or "false"
    };
    providerData?: {
      accessToken?: string;
      expiresOn?: string;
      tokenType?: string;
    };
  };
  version?: number;
}

interface AdvancedContentProps {
  children: React.ReactNode;
}

/**
 * SSR-safe component that protects content based on emailVerified status
 * Used in MDX files to conditionally render advanced content
 *
 * This component DOES NOT use window.location to avoid SSR errors.
 * Instead, it renders the Unauthorized component when access is denied.
 */
const AdvancedContent: React.FC<AdvancedContentProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    // Only run on client side
    if (typeof window === "undefined") {
      setIsLoading(false);
      return;
    }

    try {
      // Check if localStorage is available (client-side only)
      if (typeof localStorage === "undefined") {
        setIsLoading(false);
        return;
      }

      const authString = localStorage.getItem("auth-state");
      if (!authString) {
        console.log("[AdvancedContent] No auth state found");
        setIsLoading(false);
        return;
      }

      const authData: AuthState = JSON.parse(authString);
      const auth = authData?.state;

      if (!auth) {
        console.log("[AdvancedContent] No auth state data");
        setIsLoading(false);
        return;
      }

      const isAuthenticated = auth.isAuthenticated;
      const emailVerified = auth.profile?.emailVerified;

      console.log("[AdvancedContent] isAuthenticated:", isAuthenticated);
      console.log("[AdvancedContent] emailVerified:", emailVerified);

      // If authenticated and email verified (string "true"), allow access
      if (isAuthenticated && emailVerified === "true") {
        console.log("[AdvancedContent] Access granted - email verified");
        setIsAuthorized(true);
      } else if (isAuthenticated && emailVerified === "false") {
        console.log("[AdvancedContent] Access denied - email not verified");
        setIsAuthorized(false);
      }
    } catch (error) {
      console.error("[AdvancedContent] Error reading auth state:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // During SSR or initial loading, show loading state
  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "40vh",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            color: "#6b7280",
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M21 12a9 9 0 11-6.219-8.56" />
            <animateTransform
              attributeName="transform"
              attributeType="XML"
              type="rotate"
              dur="1s"
              from="0 12 12"
              to="360 12 12"
              repeatCount="indefinite"
            />
          </svg>
          Verifying access...
        </div>
      </div>
    );
  }

  // Show content only if authorized
  if (isAuthorized) {
    return <>{children}</>;
  }

  // Show unauthorized page if not authorized (no window.location redirect)
  return <Unauthorized />;
};

export default AdvancedContent;
